import { collection, addDoc, getDocs, doc, setDoc, query, orderBy, updateDoc } from 'firebase/firestore';
import { db } from './config';
import { verifyAdmin } from './auth';
import type { Course } from '../../types/course';
import { localStore } from '../storage';

export async function fetchCourses() {
  try {
    const coursesRef = collection(db, 'courses');
    const q = query(coursesRef, orderBy('order', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Course[];
  } catch (error) {
    console.error('Erreur de chargement des cours:', error);
    return [];
  }
}

export async function saveCourse(courseData: Partial<Course>) {
  try {
    // Sauvegarder localement d'abord
    localStore.saveTempCourse(courseData);

    // Vérifier l'admin
    await verifyAdmin();

    // Préparer les données du cours
    const courseToSave = {
      titre: courseData.titre || 'Nouvelle formation',
      description: courseData.description || 'Description de la formation',
      image: courseData.image || 'https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6?auto=format&fit=crop&q=80&w=1200',
      categories: courseData.categories || [],
      niveau: courseData.niveau || 'Débutant',
      prix: courseData.prix || 0,
      lien: courseData.lien || '#',
      tags: courseData.tags || [],
      rating: courseData.rating || 5,
      order: courseData.order || 0,
      hasVideo: courseData.hasVideo || false,
      hasLiveClasses: courseData.hasLiveClasses || false,
      createdAt: new Date().toISOString()
    };

    // Créer le document dans Firestore
    const coursesRef = collection(db, 'courses');
    const docRef = await addDoc(coursesRef, courseToSave);
    
    // Nettoyer le stockage temporaire
    localStore.clearTempCourses();

    // Retourner le cours créé avec son ID
    return { id: docRef.id, ...courseToSave };
  } catch (error) {
    console.error('Erreur de sauvegarde:', error);
    throw error;
  }
}

export async function updateCourse(courseId: string, courseData: Partial<Course>) {
  try {
    await verifyAdmin();
    const courseRef = doc(db, 'courses', courseId);
    await setDoc(courseRef, { ...courseData, updatedAt: new Date().toISOString() }, { merge: true });
    return { id: courseId, ...courseData };
  } catch (error) {
    console.error('Erreur de mise à jour:', error);
    throw error;
  }
}

export async function updateCourseOrder(courseId: string, newOrder: number) {
  try {
    await verifyAdmin();
    const courseRef = doc(db, 'courses', courseId);
    await updateDoc(courseRef, { 
      order: newOrder,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Erreur de mise à jour de l\'ordre:', error);
    throw error;
  }
}

export async function deleteCourse(courseId: string) {
  try {
    await verifyAdmin();
    const courseRef = doc(db, 'courses', courseId);
    await setDoc(courseRef, { deleted: true, deletedAt: new Date().toISOString() }, { merge: true });
    return true;
  } catch (error) {
    console.error('Erreur de suppression:', error);
    throw error;
  }
}